import {
  LogOutIcon,
  MessageSquareWarningIcon,
  MessageSquareQuoteIcon,
  CircleHelpIcon,
  RefreshCcwIcon,
  HousePlusIcon,
  Settings,
  ScrollTextIcon,
} from "lucide-react";
import AccountMenuDropdown from "./AccountMenuDropdown";
import AccountMenuDrawer from "./AccountMenuDrawer";
import { AccountMenuItem } from "./AccountMenuItem";
import { useTranslation } from "react-i18next";
import { useBeforeInstallPromptState } from "@/zustand/BeforeInstallPromptState";
import { useCallback, useMemo, useState } from "react";
import { IS_IOS_INSTALLABLE } from "@/lib/environmentDetector";
import { logoutUser } from "@/middleware/auth";
import { useNavigate } from "react-router-dom";
import InstallAppDialog from "@/components/shared/InstallAppDialog/InstallAppDialog";

import UserAvatar from "@/components/shared/UserAvatar";
import { ActiveNavMenu, useNavigationState } from "@/zustand/NavigationState";
import { useAuthState } from "@/zustand/AuthState";
import AddFeedback from "../left-column/feedback/AddFeedback";
import TermsModal from "@/components/shared/TermsModal/TermsModal";

export default function AccountMenu() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const username = useAuthState((state) => state.username);

  const [showFeedback, setShowFeedback] = useState(false);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const setActiveNavMenu = useNavigationState(
    (state) => state.setActiveNavMenu,
  );
  const activeMenuType = useNavigationState((state) => state.activeNavMenu);

  const { isInstallable, promptInstall } = useBeforeInstallPromptState();

  const handleInstallClick = useCallback(() => {
    if (isInstallable) {
      promptInstall();
      return;
    }
    if (IS_IOS_INSTALLABLE) {
      setShowInstallDialog(true);
    }
  }, [isInstallable, promptInstall]);

  const icons: Record<string, JSX.Element> = useMemo(
    () => ({
      install: <HousePlusIcon />,
      reload: <RefreshCcwIcon />,
      feedback: <MessageSquareQuoteIcon />,
      report: <MessageSquareWarningIcon />,
      help: <CircleHelpIcon />,
      termsAndConditions: <ScrollTextIcon />,
      settings: <Settings />,
      logout: <LogOutIcon />,
    }),
    [],
  );

  const items = useMemo(() => {
    const createItem = (
      data: Partial<AccountMenuItem> & Pick<AccountMenuItem, "key">,
    ) => {
      const result: AccountMenuItem = {
        get label() {
          return t(data.key);
        },
        disabled: false,
        variant: "ghost",
        icon: icons[data.key],
        onClick: () => {
          // console.log(data.key);
        },
        ...data,
      };
      return result;
    };

    const install = createItem({
      key: "install",
      onClick: handleInstallClick,
    });

    const reload = createItem({
      key: "reload",
      onClick: () => {
        window.location.reload();
      },
    });

    const feedback = createItem({
      key: "feedback",
      onClick: () => {
        queueMicrotask(() => {
          setShowFeedback(true);
        });
      },
    });

    // const report = createItem({
    //   key: "report",
    //   onClick: () => {
    //     // console.log("report");
    //   },
    // });

    const help = createItem({
      key: "help",
      onClick: () => {
        window.open(
          "https://help.crownsterling.io/knowledge/orion-messenger",
          "_blank",
        );
      },
    });

    const termsAndConditions = createItem({
      key: "termsAndConditions",
      onClick: () => {
        queueMicrotask(() => {
          setShowTerms(true);
        });
      },
    });

    const settings = createItem({
      key: "settings",
      onClick: () => setActiveNavMenu(ActiveNavMenu.SETTINGS),
      disabled: activeMenuType === ActiveNavMenu.SETTINGS,
    });

    const logout = createItem({
      key: "logout",
      onClick: () => {
        logoutUser();
        navigate("/login", { replace: true });
      },
      variant: "destructive",
    });

    const list: AccountMenuItem[] = [
      reload,
      feedback,
      help,
      termsAndConditions,
      settings,
      logout,
    ];

    if (IS_IOS_INSTALLABLE || isInstallable) {
      list.unshift(install);
    }

    return list;
  }, [
    handleInstallClick,
    activeMenuType,
    isInstallable,
    icons,
    t,
    setActiveNavMenu,
    navigate,
  ]);

  // Filter out the "settings" item for dropdown
  const itemsWithoutSettings = items.filter((item) => item.key !== "settings");

  const trigger = (
    <UserAvatar
      username={username}
      className="mx-auto size-8 items-center overflow-hidden rounded-full"
      aria-label={t("aria.accountMenu")}
    />
  );

  return (
    <li className="flex grow items-center justify-center self-center align-middle lg:grow-0">
      <AddFeedback
        open={showFeedback}
        onOpenChange={(e) => setShowFeedback(e)}
      />
      <InstallAppDialog
        open={showInstallDialog}
        onOpenChange={(open) => setShowInstallDialog(open)}
      />
      <TermsModal open={showTerms} onOpenChange={setShowTerms} />
      <div className="hidden lg:block">
        <AccountMenuDropdown items={itemsWithoutSettings} trigger={trigger} />
      </div>
      <div className="block lg:hidden">
        <AccountMenuDrawer items={items} trigger={trigger} />
      </div>
    </li>
  );
}
